import FacebookIcon from '@src/svgs/FacebookIcon';
import InstagramIcon from '@src/svgs/InstagramIcon';
import LinkedInIcon from '@src/svgs/LinkedInIcon';
import ThreadsIcon from '@src/svgs/ThreadsIcon';
import TiktokIcon from '@src/svgs/TiktokIcon';
import XIcon from '@src/svgs/XIcon';
import YouTubeIcon from '@src/svgs/YouTubeIcon';
import React from 'react';

import type { SafeZoneChannel } from './types';

export interface ChannelIconProps {
  size?: number;
  color?: string;
}

/**
 * Distribution-channel logo. Uses the in-codebase brand SVGs.
 *
 * Sizing: pass `size` in px (defaults to 16). Color flows through each
 * underlying icon's `fillColor` prop, defaulting to `currentColor` so the
 * icon inherits the parent's text color.
 */

interface IconRendererProps {
  size: number;
  color: string;
}

type IconRenderer = (props: IconRendererProps) => React.ReactElement;

/**
 * Map each `SafeZoneChannel` to its renderer. Centralized here so the
 * dropdown, future settings surfaces, and any analytics chip can pick the
 * same logo for the same channel without duplicating the wiring.
 */
const RENDERERS: Record<SafeZoneChannel, IconRenderer> = {
  linkedin: ({ size, color }) => (
    <LinkedInIcon width={`${size}`} height={`${size}`} fillColor={color} />
  ),
  x: ({ size, color }) => (
    <XIcon width={`${size}`} height={`${size}`} fillColor={color} />
  ),
  tiktok: ({ size, color }) => (
    <TiktokIcon width={`${size}`} height={`${size}`} fillColor={color} />
  ),
  instagram: ({ size, color }) => (
    // `InstagramIcon` paints its outer rounded-square frame from
    // `strokeColor` (it's actually a `fill` attribute, despite the prop
    // name) and the inner camera glyph from `fillColor`. Route the row
    // color to the frame so the icon lights up like YouTube's red badge;
    // pin the camera glyph to the surface background so it reads as a
    // cutout instead of vanishing into the frame.
    <InstagramIcon
      width={`${size}`}
      height={`${size}`}
      strokeColor={color}
      fillColor="var(--background)"
    />
  ),
  facebook: ({ size, color }) => (
    <FacebookIcon width={`${size}`} height={`${size}`} fillColor={color} />
  ),
  threads: ({ size, color }) => (
    <ThreadsIcon width={`${size}`} height={`${size}`} fillColor={color} />
  ),
  youtube: ({ size, color }) => (
    <YouTubeIcon width={`${size}`} height={`${size}`} fillColor={color} />
  ),
};

export const ChannelIcon: React.FC<{
  channel: SafeZoneChannel;
  size?: number;
  color?: string;
}> = ({ channel, size = 16, color = 'currentColor' }) => {
  const renderer = RENDERERS[channel];
  return renderer({ size, color });
};
